import type { WizardOperation, WizardPreview, WizardProvisionedCredential } from "@/types/wizard";

const NO_CHANGE_OUTCOMES = new Set(["exists", "skipped"]);

export function isPendingChangeOutcome(outcome: string | null | undefined): boolean {
  const normalized = (outcome ?? "").trim().toLowerCase();
  if (!normalized || NO_CHANGE_OUTCOMES.has(normalized)) {
    return false;
  }

  if (
    normalized === "planned" ||
    normalized === "created" ||
    normalized === "updated" ||
    normalized === "rotated" ||
    normalized === "configured" ||
    normalized === "warning"
  ) {
    return true;
  }

  return normalized.includes("fail") || normalized.includes("error");
}

export function getPendingChanges(preview: WizardPreview | null | undefined): WizardOperation[] {
  if (!preview) {
    return [];
  }

  if (preview.pendingChanges?.length) {
    return preview.pendingChanges;
  }

  return preview.operations.filter((op) => isPendingChangeOutcome(op.outcome));
}

export function getUnchangedCount(preview: WizardPreview | null | undefined): number {
  if (!preview) {
    return 0;
  }

  if (typeof preview.unchangedCount === "number") {
    return preview.unchangedCount;
  }

  return preview.operations.filter((op) => !isPendingChangeOutcome(op.outcome)).length;
}

export function hasPendingChanges(preview: WizardPreview | null | undefined): boolean {
  if (!preview) {
    return false;
  }

  if (typeof preview.hasPendingChanges === "boolean") {
    return preview.hasPendingChanges;
  }

  return getPendingChanges(preview).length > 0;
}

export function getRelevantCredentials(
  preview: WizardPreview | null | undefined,
  pendingChanges: WizardOperation[],
): WizardProvisionedCredential[] {
  if (!preview?.provisionedCredentials.length) {
    return [];
  }

  const pendingStages = new Set(pendingChanges.map((op) => op.stage.toLowerCase()));

  return preview.provisionedCredentials.filter((credential) => {
    if (credential.secretGenerated || credential.clientId === "pending") {
      return true;
    }

    const key = credential.key.toLowerCase();
    return [...pendingStages].some(
      (stage) => stage.includes(key) || key.includes(stage.replace(/^application-/, "")),
    );
  });
}
